import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { commissionAPI, officeAdminAPI, companyAPI } from '../../services/api';

const TABS = ['ملفات المكاتب', 'ملفات الشركات', 'المعاملات', 'عزو العملاء'];

const CALC_BASIS_LABELS = {
  TICKET_PRICE:           'نسبة من سعر التذكرة',
  SAFER_GROSS_COMMISSION: 'نسبة من صافي سافر الإجمالي (موصى به)',
  FIXED_PER_TICKET:       'مبلغ ثابت لكل تذكرة',
};

const fmt = (n) => parseFloat(n || 0).toLocaleString('ar-SD', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Status badges ─────────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const map = {
    COMPANY_TO_SAFER:         { label: 'عمولة شركات',  cls: 'bg-sky-100 text-sky-700' },
    SAFER_TO_BOOKING_OFFICE:  { label: 'عمولة مكتب',   cls: 'bg-blue-100 text-blue-700' },
    COMPANY_NET:              { label: 'صافي الشركة',  cls: 'bg-slate-100 text-slate-600' },
  };
  const { label, cls } = map[type] || { label: type, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${cls}`}>{label}</span>;
};

const SettleBadge = ({ status }) => {
  const cfg = {
    pre_ticket: { label: 'قبل التذكرة', cls: 'bg-slate-100 text-slate-500' },
    pending:    { label: 'معلق',         cls: 'bg-amber-100 text-amber-700' },
    payable:    { label: 'مستحق',        cls: 'bg-blue-100 text-blue-700' },
    paid:       { label: 'مدفوع',        cls: 'bg-emerald-100 text-emerald-700' },
    settled:    { label: 'مسوّى',        cls: 'bg-emerald-100 text-emerald-700' },
    reversed:   { label: 'معكوس',        cls: 'bg-purple-100 text-purple-700' },
    cancelled:  { label: 'ملغى',         cls: 'bg-red-100 text-red-700' },
  };
  const { label, cls } = cfg[status] || { label: status, cls: 'bg-gray-100 text-gray-500' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${cls}`}>{label}</span>;
};

const StatCard = ({ label, value, color = 'blue', unit = '' }) => {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    green:  'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber:  'bg-amber-50 border-amber-200 text-amber-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
  };
  return (
    <div className={`border rounded-xl p-4 text-center ${colors[color]}`}>
      <p className="text-2xl font-extrabold tabular-nums">{value}{unit}</p>
      <p className="text-xs font-semibold mt-1 opacity-75">{label}</p>
    </div>
  );
};

// ── Live calculation preview ──────────────────────────────────────────────────
const CalcPreview = ({ officeRate, calculationBasis, minimum, maximum }) => {
  const rate   = parseFloat(officeRate) || 0;
  const min    = minimum  != null && minimum  !== '' ? parseFloat(minimum)  : null;
  const max    = maximum  != null && maximum  !== '' ? parseFloat(maximum)  : null;
  const ticket = 50000;
  const cRate  = 10;

  const saferGross = ticket * cRate / 100;
  const base   = (calculationBasis === 'SAFER_GROSS_COMMISSION') ? saferGross : ticket;
  let   offAmt = base * rate / 100;
  if (min != null && offAmt < min) offAmt = min;
  if (max != null && offAmt > max) offAmt = max;
  const saferNet   = saferGross - offAmt;
  const companyNet = ticket - saferGross;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs mt-1">
      <p className="font-bold text-amber-800 mb-2">
        معاينة حسابية — مثال: تذكرة {fmt(ticket)} ج.س، عمولة الشركة {cRate}%
      </p>
      <div className="space-y-1 font-mono text-amber-900">
        <div className="flex justify-between">
          <span className="text-amber-700">صافي سافر الإجمالي ({cRate}%):</span>
          <span className="font-bold">{fmt(saferGross)} ج.س</span>
        </div>
        <div className="flex justify-between">
          <span className="text-amber-700">أساس الحساب:</span>
          <span>{calculationBasis === 'SAFER_GROSS_COMMISSION' ? 'صافي سافر الإجمالي' : 'سعر التذكرة'}</span>
        </div>
        <div className="flex justify-between text-orange-700">
          <span>عمولة المكتب ({rate}% × {fmt(base)}):</span>
          <span className="font-bold">{fmt(offAmt)} ج.س</span>
        </div>
        <div className="flex justify-between text-emerald-700 border-t border-amber-200 pt-1">
          <span>صافي سافر النهائي:</span>
          <span className="font-bold">{fmt(saferNet)} ج.س</span>
        </div>
        <div className="flex justify-between text-slate-600">
          <span>صافي الشركة:</span>
          <span>{fmt(companyNet)} ج.س</span>
        </div>
      </div>
    </div>
  );
};

// ── Profile Form ──────────────────────────────────────────────────────────────
const ProfileForm = ({ type, onSave, onCancel, initial = {} }) => {
  const isOffice = type === 'office';
  const [form, setForm] = useState({
    name:                   initial.name                   || '',
    validUntil:             initial.validUntil             || '',
    officeBookingRate:      initial.officeBookingRate      ?? '',
    referralRate:           initial.referralRate           ?? '',
    referralValidityMonths: initial.referralValidityMonths ?? 12,
    passengerPortalRate:    initial.passengerPortalRate    ?? '',
    whatsappRate:           initial.whatsappRate           ?? '',
    mobileAppRate:          initial.mobileAppRate          ?? '',
    companyPortalRate:      initial.companyPortalRate      ?? '',
    calculationBasis:       initial.calculationBasis       || 'SAFER_GROSS_COMMISSION',
    minimumCommission:      initial.minimumCommission      ?? '',
    maximumCommission:      initial.maximumCommission      ?? '',
    priority:               initial.priority               ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handle = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('اسم الملف التعريفي مطلوب');
    setSaving(true);
    try { await onSave({ type, ...form }); }
    finally { setSaving(false); }
  };

  const inp = (key, label, opts = {}) => (
    <div key={key}>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input
        type={opts.date ? 'date' : 'number'}
        min={opts.date ? undefined : 0}
        max={opts.date ? undefined : (opts.noCap ? undefined : 100)}
        step={opts.integer ? 1 : 0.01}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        placeholder={opts.placeholder || ''}
      />
    </div>
  );

  return (
    <form onSubmit={handle} className="bg-blue-50 border border-blue-200 rounded-2xl p-5 space-y-4">
      <h3 className="text-sm font-bold text-blue-800">
        {initial.id ? 'تعديل الملف التعريفي' : 'إنشاء ملف تعريفي جديد'}
        {initial.version ? <span className="ml-2 font-normal text-blue-600">v{initial.version}</span> : null}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-600 mb-1">اسم الملف التعريفي *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder={isOffice ? 'مثال: عمولة مكتب قياسي' : 'مثال: خطة شركة أساسية'}
            required
          />
        </div>

        {isOffice ? (
          <>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">أساس حساب عمولة المكتب</label>
              <select
                value={form.calculationBasis}
                onChange={(e) => set('calculationBasis', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {Object.entries(CALC_BASIS_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>

            {inp('officeBookingRate', 'سافر تدفع للمكتب — حجز مباشر %')}
            {inp('referralRate',      'سافر تدفع للمكتب — إحالة %')}
            {inp('referralValidityMonths', 'مدة صلاحية الإحالة (أشهر)', { integer: true, noCap: true })}
            {inp('minimumCommission', 'حد أدنى للعمولة (ج.س)', { noCap: true, placeholder: 'اختياري' })}
            {inp('maximumCommission', 'حد أقصى للعمولة (ج.س)', { noCap: true, placeholder: 'اختياري' })}
            {inp('priority', 'أولوية التطبيق (رقم أعلى = أولوية أعلى)', { integer: true, noCap: true })}
            {inp('validUntil', 'صالح حتى تاريخ (اختياري)', { date: true })}

            {/* Live preview */}
            {form.officeBookingRate !== '' && (
              <div className="sm:col-span-2">
                <CalcPreview
                  officeRate={form.officeBookingRate}
                  calculationBasis={form.calculationBasis}
                  minimum={form.minimumCommission}
                  maximum={form.maximumCommission}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {inp('passengerPortalRate', 'عمولة الشركة على كل تذكرة % (جميع القنوات)')}
            {inp('minimumCommission', 'حد أدنى للعمولة (ج.س)', { noCap: true, placeholder: 'اختياري' })}
            {inp('maximumCommission', 'حد أقصى للعمولة (ج.س)', { noCap: true, placeholder: 'اختياري' })}
            {inp('priority', 'أولوية التطبيق', { integer: true, noCap: true })}
            {inp('validUntil', 'صالح حتى تاريخ (اختياري)', { date: true })}
          </>
        )}
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition">
          {saving ? 'جاري الحفظ...' : 'حفظ'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 bg-white border border-slate-200 text-sm font-bold rounded-xl hover:bg-slate-50 transition">
          إلغاء
        </button>
      </div>
    </form>
  );
};

// ── Assign Dialog ─────────────────────────────────────────────────────────────
const AssignDialog = ({ profile, entities, entityType, onAssign, onClose }) => {
  const [entityId, setEntityId] = useState('');
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!entityId) return toast.error('اختر العنصر أولاً');
    setSaving(true);
    try {
      await onAssign(profile.id, entityType, entityId);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl" dir="rtl">
        <h3 className="font-bold text-slate-800 mb-4">
          تعيين "{profile.name}" إلى {entityType === 'office' ? 'مكتب' : 'شركة'}
        </h3>
        <select
          value={entityId} onChange={(e) => setEntityId(e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">اختر...</option>
          {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <div className="flex gap-2">
          <button onClick={handle} disabled={saving}
            className="flex-1 bg-blue-600 text-white py-2 rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition">
            {saving ? 'جاري...' : 'تعيين'}
          </button>
          <button onClick={onClose}
            className="flex-1 border border-slate-200 py-2 rounded-xl text-sm font-bold hover:bg-slate-50 transition">
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Reversal Dialog ───────────────────────────────────────────────────────────
const ReversalDialog = ({ txn, onDone, onClose }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!reason.trim()) return toast.error('سبب الإلغاء مطلوب');
    setSaving(true);
    try {
      await commissionAPI.reverseTransaction(txn.id, reason);
      toast.success('تم إنشاء سجل الإلغاء');
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل إنشاء الإلغاء');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl" dir="rtl">
        <h3 className="font-bold text-slate-800 mb-1">عكس المعاملة</h3>
        <p className="text-xs text-slate-500 mb-4">
          معاملة #{txn.id?.slice(0, 8)}… | {fmt(txn.commissionAmount)} ج.س
        </p>
        <label className="block text-xs font-semibold text-slate-600 mb-1">سبب الإلغاء *</label>
        <textarea
          value={reason} onChange={(e) => setReason(e.target.value)}
          rows={3} placeholder="مثال: إلغاء الحجز بطلب العميل"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
        />
        <div className="flex gap-2">
          <button onClick={handle} disabled={saving}
            className="flex-1 bg-red-600 text-white py-2 rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition">
            {saving ? 'جاري...' : 'تأكيد الإلغاء'}
          </button>
          <button onClick={onClose}
            className="flex-1 border border-slate-200 py-2 rounded-xl text-sm font-bold hover:bg-slate-50 transition">
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Tab: Office Profiles ──────────────────────────────────────────────────────
const OfficeProfilesTab = () => {
  const [profiles, setProfiles] = useState([]);
  const [offices, setOffices]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([commissionAPI.listProfiles('office'), officeAdminAPI.list({})])
      .then(([p, o]) => { setProfiles(p.data.data || []); setOffices(o.data.data || []); })
      .catch(() => toast.error('فشل التحميل'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editing) {
        await commissionAPI.updateProfile(editing.id, data);
        toast.success('تم تحديث الملف التعريفي');
        setEditing(null);
      } else {
        await commissionAPI.createProfile(data);
        toast.success('تم إنشاء الملف التعريفي');
        setShowForm(false);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل الحفظ');
      throw e;
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('إلغاء تفعيل هذا الملف التعريفي؟')) return;
    try { await commissionAPI.deactivateProfile(id); toast.success('تم إلغاء التفعيل'); load(); }
    catch { toast.error('فشل الإلغاء'); }
  };

  const handleAssign = async (profileId, entityType, entityId) => {
    await commissionAPI.assignProfile({ profileId, entityType, entityId });
    toast.success('تم تعيين الملف التعريفي');
    load();
  };

  const handleUnassign = async (entityId) => {
    try { await commissionAPI.unassignProfile({ entityType: 'office', entityId }); toast.success('تم إلغاء التعيين'); load(); }
    catch { toast.error('فشل إلغاء التعيين'); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">جاري التحميل...</div>;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">
          ملفات قابلة لإعادة الاستخدام — حدد أساس الحساب <strong>صافي سافر الإجمالي</strong> للنموذج الصحيح.
        </p>
        <button onClick={() => { setShowForm(true); setEditing(null); }}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition">
          + ملف جديد
        </button>
      </div>

      {(showForm && !editing) && (
        <ProfileForm type="office" onSave={handleSave} onCancel={() => setShowForm(false)} />
      )}

      {profiles.length === 0 && !showForm && (
        <div className="text-center py-12 text-slate-400 bg-white border border-slate-200 rounded-2xl">
          لا توجد ملفات تعريفية للمكاتب بعد
        </div>
      )}

      <div className="space-y-4">
        {profiles.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {editing?.id === p.id ? (
              <div className="p-4">
                <ProfileForm type="office" initial={editing} onSave={handleSave} onCancel={() => setEditing(null)} />
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between p-4 border-b border-slate-100">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-slate-800">{p.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${p.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {p.isActive ? 'نشط' : 'موقوف'}
                      </span>
                      {p.version && (
                        <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">v{p.version}</span>
                      )}
                      {p.priority > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full font-medium">أولوية {p.priority}</span>
                      )}
                      {p.validUntil && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${new Date(p.validUntil) < new Date() ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                          حتى {p.validUntil}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      {p.calculationBasis && (
                        <span className={`px-2 py-0.5 rounded-lg font-medium ${p.calculationBasis === 'SAFER_GROSS_COMMISSION' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
                          {CALC_BASIS_LABELS[p.calculationBasis] || p.calculationBasis}
                        </span>
                      )}
                      {p.officeBookingRate != null && (
                        <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded-lg font-medium">
                          حجز: {parseFloat(p.officeBookingRate)}%
                        </span>
                      )}
                      {p.referralRate != null && (
                        <span className="bg-teal-50 text-teal-700 px-2 py-0.5 rounded-lg font-medium">
                          إحالة: {parseFloat(p.referralRate)}%
                        </span>
                      )}
                      {p.minimumCommission != null && (
                        <span className="bg-slate-50 text-slate-600 px-2 py-0.5 rounded-lg font-medium">
                          حد أدنى: {fmt(p.minimumCommission)} ج.س
                        </span>
                      )}
                      {p.maximumCommission != null && (
                        <span className="bg-slate-50 text-slate-600 px-2 py-0.5 rounded-lg font-medium">
                          حد أقصى: {fmt(p.maximumCommission)} ج.س
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setAssignTarget(p)}
                      className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold">
                      تعيين لمكتب
                    </button>
                    <button onClick={() => setEditing(p)}
                      className="text-xs px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition">
                      تعديل
                    </button>
                    {p.isActive && (
                      <button onClick={() => handleDeactivate(p.id)}
                        className="text-xs px-3 py-1.5 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition">
                        إيقاف
                      </button>
                    )}
                  </div>
                </div>

                {(p.assignedOffices || []).length > 0 && (
                  <div className="px-4 py-3 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-500 mb-2">المكاتب المعينة ({p.assignedOffices.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {p.assignedOffices.map((o) => (
                        <div key={o.id} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs">
                          <span className="text-slate-700 font-medium">{o.name}</span>
                          {o.city && <span className="text-slate-400">{o.city}</span>}
                          <button onClick={() => handleUnassign(o.id)}
                            className="text-slate-400 hover:text-red-500 transition text-xs font-bold mr-1">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {assignTarget && (
        <AssignDialog profile={assignTarget} entities={offices} entityType="office"
          onAssign={handleAssign} onClose={() => setAssignTarget(null)} />
      )}
    </div>
  );
};

// ── Tab: Company Profiles ─────────────────────────────────────────────────────
const CompanyProfilesTab = () => {
  const [profiles, setProfiles]   = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([commissionAPI.listProfiles('company'), companyAPI.getAll()])
      .then(([p, c]) => { setProfiles(p.data.data || []); setCompanies(c.data.data || []); })
      .catch(() => toast.error('فشل التحميل'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editing) {
        await commissionAPI.updateProfile(editing.id, data);
        toast.success('تم تحديث الملف التعريفي');
        setEditing(null);
      } else {
        await commissionAPI.createProfile(data);
        toast.success('تم إنشاء الملف التعريفي');
        setShowForm(false);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل الحفظ');
      throw e;
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('إلغاء تفعيل هذا الملف التعريفي؟')) return;
    try { await commissionAPI.deactivateProfile(id); toast.success('تم إلغاء التفعيل'); load(); }
    catch { toast.error('فشل الإلغاء'); }
  };

  const handleAssign = async (profileId, entityType, entityId) => {
    await commissionAPI.assignProfile({ profileId, entityType, entityId });
    toast.success('تم تعيين الملف التعريفي');
    load();
  };

  const handleUnassign = async (entityId) => {
    try { await commissionAPI.unassignProfile({ entityType: 'company', entityId }); toast.success('تم إلغاء التعيين'); load(); }
    catch { toast.error('فشل إلغاء التعيين'); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">جاري التحميل...</div>;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">ملفات تعريفية للشركات — النسبة التي تدفعها الشركة لسافر على كل تذكرة مُصدرة.</p>
        <button onClick={() => { setShowForm(true); setEditing(null); }}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition">
          + ملف جديد
        </button>
      </div>

      {(showForm && !editing) && (
        <ProfileForm type="company" onSave={handleSave} onCancel={() => setShowForm(false)} />
      )}

      {profiles.length === 0 && !showForm && (
        <div className="text-center py-12 text-slate-400 bg-white border border-slate-200 rounded-2xl">
          لا توجد ملفات تعريفية للشركات بعد
        </div>
      )}

      <div className="space-y-4">
        {profiles.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {editing?.id === p.id ? (
              <div className="p-4">
                <ProfileForm type="company" initial={editing} onSave={handleSave} onCancel={() => setEditing(null)} />
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between p-4 border-b border-slate-100">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-slate-800">{p.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${p.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {p.isActive ? 'نشط' : 'موقوف'}
                      </span>
                      {p.version && (
                        <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">v{p.version}</span>
                      )}
                      {p.validUntil && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${new Date(p.validUntil) < new Date() ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                          حتى {p.validUntil}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      {p.passengerPortalRate != null && (
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-lg font-medium">
                          عمولة التذكرة: {parseFloat(p.passengerPortalRate)}%
                        </span>
                      )}
                      {p.minimumCommission != null && (
                        <span className="bg-slate-50 text-slate-600 px-2 py-0.5 rounded-lg font-medium">
                          حد أدنى: {fmt(p.minimumCommission)} ج.س
                        </span>
                      )}
                      {p.maximumCommission != null && (
                        <span className="bg-slate-50 text-slate-600 px-2 py-0.5 rounded-lg font-medium">
                          حد أقصى: {fmt(p.maximumCommission)} ج.س
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setAssignTarget(p)}
                      className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold">
                      تعيين لشركة
                    </button>
                    <button onClick={() => setEditing(p)}
                      className="text-xs px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition">
                      تعديل
                    </button>
                    {p.isActive && (
                      <button onClick={() => handleDeactivate(p.id)}
                        className="text-xs px-3 py-1.5 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition">
                        إيقاف
                      </button>
                    )}
                  </div>
                </div>

                {(p.assignedCompanies || []).length > 0 && (
                  <div className="px-4 py-3 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-500 mb-2">الشركات المعينة ({p.assignedCompanies.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {p.assignedCompanies.map((c) => (
                        <div key={c.id} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs">
                          <span className="text-slate-700 font-medium">{c.name}</span>
                          <button onClick={() => handleUnassign(c.id)}
                            className="text-slate-400 hover:text-red-500 transition text-xs font-bold mr-1">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {assignTarget && (
        <AssignDialog profile={assignTarget} entities={companies} entityType="company"
          onAssign={handleAssign} onClose={() => setAssignTarget(null)} />
      )}
    </div>
  );
};

// ── Tab: Transactions ─────────────────────────────────────────────────────────
const TransactionsTab = () => {
  const [rows, setRows]           = useState([]);
  const [meta, setMeta]           = useState({});
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [reversing, setReversing] = useState(null);
  const [filters, setFilters]     = useState({
    from: '', to: '', commissionType: '', settlementStatus: '',
  });

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 50, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
    Promise.all([
      commissionAPI.getTransactions(params),
      commissionAPI.getSummary({ from: filters.from, to: filters.to }),
    ])
      .then(([txns, sum]) => {
        setRows(txns.data.data || []);
        setMeta(txns.data.meta || {});
        setSummary(sum.data.data?.summary || null);
      })
      .catch(() => toast.error('فشل التحميل'))
      .finally(() => setLoading(false));
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil((meta.total || 0) / 50);
  const canReverse = (r) => !r.frozenAt && !r.reversalOfTransactionId && !['reversed', 'cancelled'].includes(r.settlementStatus);

  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="إجمالي العمولة"  value={fmt(summary.total)} unit=" ج.س" color="blue" />
          <StatCard label="معلق"             value={fmt(summary.pending)} unit=" ج.س" color="amber" />
          <StatCard label="مسوّى/مدفوع"     value={fmt(summary.paid)} unit=" ج.س" color="green" />
          <StatCard label="عمولة مكاتب"
            value={fmt(summary.byType?.SAFER_TO_BOOKING_OFFICE || 0)}
            unit=" ج.س" color="purple" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3">
        {[['from', 'من', 'date'], ['to', 'إلى', 'date']].map(([k, lbl, t]) => (
          <div key={k}>
            <label className="block text-xs font-semibold text-slate-500 mb-1">{lbl}</label>
            <input type={t} value={filters[k]}
              onChange={(e) => { setFilters((f) => ({ ...f, [k]: e.target.value })); setPage(1); }}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
          </div>
        ))}
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">النوع</label>
          <select value={filters.commissionType}
            onChange={(e) => { setFilters((f) => ({ ...f, commissionType: e.target.value })); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm">
            <option value="">الكل</option>
            <option value="COMPANY_TO_SAFER">عمولة شركات</option>
            <option value="SAFER_TO_BOOKING_OFFICE">عمولة مكتب</option>
            <option value="COMPANY_NET">صافي الشركة</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">الحالة</label>
          <select value={filters.settlementStatus}
            onChange={(e) => { setFilters((f) => ({ ...f, settlementStatus: e.target.value })); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm">
            <option value="">الكل</option>
            <option value="pre_ticket">قبل التذكرة</option>
            <option value="pending">معلق</option>
            <option value="payable">مستحق</option>
            <option value="paid">مدفوع</option>
            <option value="reversed">معكوس</option>
            <option value="cancelled">ملغى</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[950px]">
            <thead className="bg-slate-50 text-xs text-slate-500 font-bold uppercase">
              <tr>
                <th className="px-4 py-3 text-right">رقم الحجز</th>
                <th className="px-3 py-3 text-right">النوع</th>
                <th className="px-3 py-3 text-right">المكتب / الشركة</th>
                <th className="px-3 py-3 text-center">النسبة%</th>
                <th className="px-3 py-3 text-center">سعر التذكرة</th>
                <th className="px-3 py-3 text-center">مبلغ العمولة</th>
                <th className="px-3 py-3 text-center">صافي سافر</th>
                <th className="px-3 py-3 text-center">الحالة</th>
                <th className="px-3 py-3 text-right">التاريخ</th>
                <th className="px-3 py-3 text-center">إجراء</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">جاري التحميل...</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">لا توجد معاملات</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className={`hover:bg-slate-50 ${r.reversalOfTransactionId ? 'opacity-70 bg-purple-50/30' : ''}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-700">
                    {r.booking?.bookingNumber || '—'}
                    {r.reversalOfTransactionId && (
                      <span className="block text-purple-500 text-xs">↩ إلغاء</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5"><TypeBadge type={r.commissionType} /></td>
                  <td className="px-3 py-2.5 text-slate-600 text-xs">
                    {r.bookingOffice?.name || r.company?.name || '—'}
                  </td>
                  <td className="px-3 py-2.5 text-center tabular-nums">{r.commissionPct}%</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-slate-600">{fmt(r.baseAmount)}</td>
                  <td className={`px-3 py-2.5 text-center tabular-nums font-semibold ${parseFloat(r.commissionAmount) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                    {fmt(r.commissionAmount)}
                  </td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-blue-700 text-xs">
                    {r.saferNetCommission != null ? fmt(r.saferNetCommission) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-center"><SettleBadge status={r.settlementStatus} /></td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">
                    {new Date(r.calculatedAt).toLocaleDateString('ar-SD')}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {canReverse(r) ? (
                      <button onClick={() => setReversing(r)}
                        className="text-xs px-2 py-1 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition font-semibold">
                        عكس
                      </button>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            <span>إجمالي: {meta.total} معاملة</span>
            <div className="flex gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">‹ السابق</button>
              <span className="px-3 py-1">{page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">التالي ›</button>
            </div>
          </div>
        )}
      </div>

      {reversing && (
        <ReversalDialog txn={reversing} onDone={load} onClose={() => setReversing(null)} />
      )}
    </div>
  );
};

// ── Tab: Customer Attribution ─────────────────────────────────────────────────
const CustomersTab = () => {
  const [rows, setRows]       = useState([]);
  const [meta, setMeta]       = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [q, setQ]             = useState('');

  const load = useCallback(() => {
    setLoading(true);
    commissionAPI.listCustomers({ page, limit: 50, search: q || undefined })
      .then(({ data }) => { setRows(data.data || []); setMeta(data.meta || {}); })
      .catch(() => toast.error('فشل التحميل'))
      .finally(() => setLoading(false));
  }, [page, q]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil((meta.total || 0) / 50);

  return (
    <div className="space-y-4">
      <form onSubmit={(e) => { e.preventDefault(); setQ(search); setPage(1); }} className="flex gap-2">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث بالاسم أو رقم الهاتف..."
          className="flex-1 border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        <button type="submit"
          className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition">بحث</button>
      </form>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-50 text-xs text-slate-500 font-bold uppercase">
              <tr>
                <th className="px-4 py-3 text-right">الجوال</th>
                <th className="px-4 py-3 text-right">الاسم</th>
                <th className="px-4 py-3 text-right">مكتب الاقتناء</th>
                <th className="px-3 py-3 text-center">تاريخ الاقتناء</th>
                <th className="px-3 py-3 text-center">انتهاء الإحالة</th>
                <th className="px-3 py-3 text-center">الإحالة</th>
                <th className="px-3 py-3 text-center">إجمالي الرحلات</th>
                <th className="px-3 py-3 text-right">آخر قناة</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">جاري التحميل...</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">لا توجد نتائج</td></tr>}
              {rows.map((r) => {
                const referralActive = r.firstBookingOfficeId && (!r.referralExpiryDate || new Date(r.referralExpiryDate) >= new Date());
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-mono text-xs">{r.mobileNumber}</td>
                    <td className="px-4 py-2.5 text-slate-700">{r.fullName || '—'}</td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{r.firstOffice?.name || '—'}</td>
                    <td className="px-3 py-2.5 text-center text-xs text-slate-500">{r.acquisitionDate ? new Date(r.acquisitionDate).toLocaleDateString('ar-SD') : '—'}</td>
                    <td className="px-3 py-2.5 text-center text-xs text-slate-500">{r.referralExpiryDate ? new Date(r.referralExpiryDate).toLocaleDateString('ar-SD') : '—'}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${referralActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {referralActive ? 'نشطة' : 'منتهية'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center font-semibold tabular-nums">{r.totalTrips}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{r.lastBookingChannel || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            <span>إجمالي: {meta.total} عميل</span>
            <div className="flex gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">‹ السابق</button>
              <span className="px-3 py-1">{page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">التالي ›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CommissionPage() {
  const [tab, setTab] = useState(0);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" dir="rtl">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-slate-900">إدارة العمولات</h1>
        <p className="text-sm text-slate-500 mt-1">
          إنشاء وإدارة ملفات تعريفية للعمولات، عرض معاملات المفصّلة، وعكس العمليات.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 mb-6 w-fit">
        {TABS.map((label, i) => (
          <button key={i} onClick={() => setTab(i)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === i ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 0 && <OfficeProfilesTab />}
      {tab === 1 && <CompanyProfilesTab />}
      {tab === 2 && <TransactionsTab />}
      {tab === 3 && <CustomersTab />}
    </div>
  );
}
